import React, { useEffect, useState, useMemo } from "react";
import MessengerLayout      from "../../../layout/MessengerLayout";
import ProjectsPanel        from "../../projects/ProjectsPanel";
import KanbanBoard          from "../../projects/KanbanBoard";
import TaskListView         from "../../projects/TaskListView";
import TaskDrawer           from "../../projects/TaskDrawer";
import TeamWorkloadPanel    from "../../projects/TeamWorkloadPanel";
import ProjectAnalytics     from "../../projects/ProjectAnalytics";
import ProjectKpis          from "../../projects/ProjectKpis";
import { toast }            from "../../ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

const TABS = [
  { key: "overview",   label: "Overview"   },
  { key: "projects",   label: "Projects"   },
  { key: "tasks",      label: "Tasks"      },
  { key: "team",       label: "Team"       },
  { key: "analytics",  label: "Analytics"  },
];

export default function ProjectManagementPage() {
  const [tab,         setTab]         = useState("overview");
  const [projects,    setProjects]    = useState([]);
  const [tasks,       setTasks]       = useState([]);
  const [members,     setMembers]     = useState([]);
  const [kpis,        setKpis]        = useState(null);
  const [selectedTask,setSelectedTask]= useState(null);
  const [activeProject,setActiveProject] = useState("ALL");
  const [taskView,    setTaskView]    = useState("kanban"); // kanban | list
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    Promise.all([
      fetch(`${API}/api/projects`,          { headers: authH() }).then(r => r.json()),
      fetch(`${API}/api/projects/tasks`,    { headers: authH() }).then(r => r.json()),
      fetch(`${API}/api/projects/members`,  { headers: authH() }).then(r => r.json()),
      fetch(`${API}/api/projects/kpis`,     { headers: authH() }).then(r => r.json()),
    ])
      .then(([p, t, m, k]) => {
        setProjects(Array.isArray(p) ? p : []);
        setTasks(Array.isArray(t) ? t : []);
        setMembers(Array.isArray(m) ? m : []);
        setKpis(k || null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  /* ── Filtered tasks ── */
  const filteredTasks = useMemo(() =>
    activeProject === "ALL" ? tasks : tasks.filter(t => t.project_id === activeProject),
  [tasks, activeProject]);

  /* ── Task actions ── */
  // Returns true on success, false on failure. Used by the drawer's onSave
  // to suppress the "Task saved" toast when the PATCH actually failed.
  const patchTask = async (id, patch) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
    try {
      const res = await fetch(`${API}/api/projects/tasks/${id}`, {
        method: "PATCH", headers: authH(), body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const createTask = async (draft) => {
    try {
      const res = await fetch(`${API}/api/projects/tasks`, {
        method: "POST", headers: authH(), body: JSON.stringify(draft),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = await res.json();
      setTasks(prev => [...prev, created]);
      toast.success("Task saved");
      return created;
    } catch (e) { setError(e.message); toast.error("Failed to save task"); }
  };

  // Deletion confirmation + success/error toasts are owned by TaskDrawer's
  // own useConfirmDelete. This handler just performs the API call and
  // throws on failure so the drawer's catch block fires the error toast.
  const deleteTask = async (id) => {
    const res = await fetch(`${API}/api/projects/tasks/${id}`, { method: "DELETE", headers: authH() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    setTasks(prev => prev.filter(t => t.id !== id));
    setSelectedTask(null);
  };

  const moveTask = (id, status) => patchTask(id, { status });
  const assignTask = (id, assignee_id) => patchTask(id, { assignee_id });

  /* ── Project actions ── */
  const createProject = async (draft) => {
    const res = await fetch(`${API}/api/projects`, {
      method: "POST", headers: authH(), body: JSON.stringify(draft),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const created = await res.json();
    setProjects(prev => [...prev, created]);
    return created;
  };

  const updateProject = async (id, patch) => {
    await fetch(`${API}/api/projects/${id}`, {
      method: "PATCH", headers: authH(), body: JSON.stringify(patch),
    });
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const deleteProject = async (id) => {
    await fetch(`${API}/api/projects/${id}`, { method: "DELETE", headers: authH() });
    setProjects(prev => prev.filter(p => p.id !== id));
    if (activeProject === id) setActiveProject("ALL");
  };

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#00ff90]">Project Management</h1>
            <p className="text-white/40 text-sm mt-0.5">Tasks, projects and team performance in one place</p>
          </div>

          {/* Project filter pill */}
          {projects.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-white/30">Project:</span>
              <select
                value={activeProject}
                onChange={e => setActiveProject(e.target.value)}
                className="bg-black border border-[#00ff90]/20 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors">
                <option value="ALL">All Projects</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/5 pb-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key ? "border-[#00ff90] text-[#00ff90]" : "border-transparent text-white/40 hover:text-white/70"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

        {/* ── OVERVIEW ── */}
        {tab === "overview" && (
          <div className="space-y-5">
            <ProjectKpis kpis={kpis} loading={loading} tasks={filteredTasks} />
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className="xl:col-span-2">
                <KanbanBoard
                  tasks={filteredTasks.filter(t => t.status !== "Done").slice(0, 12)}
                  members={members}
                  onMove={moveTask}
                  onOpen={setSelectedTask}
                  compact
                />
              </div>
              <TeamWorkloadPanel
                members={members}
                tasks={filteredTasks}
                compact
              />
            </div>
          </div>
        )}

        {/* ── PROJECTS ── */}
        {tab === "projects" && (
          <ProjectsPanel
            projects={projects}
            tasks={tasks}
            members={members}
            onCreate={createProject}
            onUpdate={updateProject}
            onDelete={deleteProject}
            onSelectProject={id => { setActiveProject(id); setTab("tasks"); }}
          />
        )}

        {/* ── TASKS ── */}
        {tab === "tasks" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setTaskView("kanban")}
                className={`text-xs px-4 py-2 rounded-lg border transition-colors ${taskView === "kanban" ? "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/40 hover:text-white"}`}>
                Kanban
              </button>
              <button onClick={() => setTaskView("list")}
                className={`text-xs px-4 py-2 rounded-lg border transition-colors ${taskView === "list" ? "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/40 hover:text-white"}`}>
                List
              </button>
              <button
                onClick={() => setSelectedTask({ _new: true, project_id: activeProject !== "ALL" ? activeProject : "" })}
                className="ml-auto text-xs px-4 py-2 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20 transition-colors">
                + New Task
              </button>
            </div>

            {taskView === "kanban" ? (
              <KanbanBoard
                tasks={filteredTasks}
                members={members}
                projects={projects}
                onMove={moveTask}
                onOpen={setSelectedTask}
                onCreate={draft => setSelectedTask({ _new: true, ...draft })}
              />
            ) : (
              <TaskListView
                tasks={filteredTasks}
                members={members}
                projects={projects}
                onOpen={setSelectedTask}
                onMove={moveTask}
                onAssign={assignTask}
              />
            )}
          </div>
        )}

        {/* ── TEAM ── */}
        {tab === "team" && (
          <TeamWorkloadPanel
            members={members}
            tasks={tasks}
            projects={projects}
          />
        )}

        {/* ── ANALYTICS ── */}
        {tab === "analytics" && (
          <ProjectAnalytics
            tasks={tasks}
            members={members}
            projects={projects}
          />
        )}
      </div>

      {/* Task drawer */}
      <TaskDrawer
        open={!!selectedTask}
        task={selectedTask}
        members={members}
        projects={projects}
        onClose={() => setSelectedTask(null)}
        onSave={async (draft) => {
          try {
            if (draft._new || draft.id === undefined) {
              const created = await createTask(draft);
              if (!created) return; // createTask already toasted error
            } else {
              const ok = await patchTask(draft.id, draft);
              if (ok) toast.success("Task saved");
              else { toast.error("Failed to save task"); return; }
            }
            setSelectedTask(null);
          } catch (e) {
            toast.error("Failed to save task");
          }
        }}
        onDelete={deleteTask}
        onMove={moveTask}
      />
    </MessengerLayout>
  );
}